import React, { memo, useRef, useEffect, useCallback, useState } from "react";
import cx from "classnames";
import { get, size } from "lodash";
import Icons from "../../components/icons";
import { ICONS } from "../../constants/icons.constants";
import ThumbNail from "../../components/thumbNail/ThumbNail";
import { MEDIA_URL } from "../../axios";
import musicPlayerStore from "../../zustand/musicPlayer.store";
import likedSongsStore from "../../zustand/likedSong.store";
import ignoredSongsStore from "../../zustand/ignoreSong.store";
import playlistStore from "../../zustand/playlist.store";
import userStore from "../../zustand/user.store";
import { addSongToFavorites, removeSongFromFavorites } from "../../actions/songs.actions";
import { ignoreSong } from "../../actions/playlist.actions";
import { socketServices } from "../../services/socket.service";
import { shuffleArray } from "../../helpers/music.helpers";
import { showToaster } from "../../helpers/toaster.helpers";
import { getFormattedTime } from "../../components/musicPlayer/musicPlayer.helpers";
import styles from "./FullPlayer.module.scss";

const PlayIcon = () => (
  <svg width="30" height="30" viewBox="0 0 24 24" fill="white">
    <polygon points="6,3 20,12 6,21" />
  </svg>
);

const PauseIcon = () => (
  <svg width="30" height="30" viewBox="0 0 24 24" fill="white">
    <rect x="5" y="3" width="5" height="18" rx="1" />
    <rect x="14" y="3" width="5" height="18" rx="1" />
  </svg>
);

const PrevIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="white">
    <polygon points="19,3 9,12 19,21" />
    <rect x="5" y="3" width="3" height="18" rx="1" />
  </svg>
);

const NextIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="white">
    <polygon points="5,3 15,12 5,21" />
    <rect x="16" y="3" width="3" height="18" rx="1" />
  </svg>
);

const FullPlayer = ({ onClose }) => {
  const {
    songs, currentSongIndex, isPlaying, setIsPlaying,
    setCurrentSongIndex, playList, setSongs,
    toggleIsShuffle, isShuffle,
    searchedSong,
  } = musicPlayerStore((state) => state);

  const { songs: likedSongs, addSong: addLikedSong, deleteSong: deleteLikedSong } = likedSongsStore();
  const { songs: ignoredSongs, addSong: addIgnoreSong, deleteSong: removeIgnoreSong } = ignoredSongsStore();
  const { addLikedSongToPlaylist, removeUnLikedSongFromPlaylist } = playlistStore((state) => state);
  const { userOffline } = userStore((state) => ({ userOffline: state.userOffline }));

  const progressRef = useRef(null);
  const currentTimeRef = useRef(null);
  const totalTimeRef = useRef(null);
  const containerRef = useRef(null);
  const touchStartY = useRef(0);
  const touchDelta = useRef(0);
  const [isMuted, setIsMuted] = useState(false);

  const song = searchedSong || songs?.[currentSongIndex];
  const isLiked = likedSongs?.[song?.id];
  const isIgnored = get(ignoredSongs, song?.id + playList?.id, false);

  useEffect(() => {
    const engine = window.__4playEngine;
    if (!engine) return;
    const id = setInterval(() => {
      const ref = engine.getCurrentAudioRef();
      if (!ref?.current || !progressRef.current) return;
      const current = ref.current.currentTime;
      const total = ref.current.duration;
      if (!isNaN(total) && total > 0) {
        const pct = current / total;
        progressRef.current.value = pct;
        progressRef.current.style.setProperty('--progress-pct', (pct * 100) + '%');
        if (currentTimeRef.current) currentTimeRef.current.textContent = getFormattedTime(current);
        if (totalTimeRef.current) totalTimeRef.current.textContent = getFormattedTime(total);
      }
    }, 250);
    return () => clearInterval(id);
  }, []);

  const handlePlayPause = () => {
    if (window.__4playEngine) window.__4playEngine.primeElements();
    const willPlay = !isPlaying;
    setIsPlaying(willPlay);
    const el = window.__4playEngine?.getCurrentAudioRef()?.current;
    if (willPlay && el) {
      el.muted = false;
      if (window.__4playEngine) window.__4playEngine.setGainValue(window.__4playEngine.getCurrentGain(), 1);
      el.play().catch(() => {});
    } else if (!willPlay && el) {
      el.pause();
    }
    if (userOffline) return;
    try {
      if (isPlaying) socketServices.pauseSong();
      else {
        const payload = { song_id: songs?.[currentSongIndex]?.id };
        if (playList) payload.playlist_id = playList.id;
        socketServices.changeSong(payload);
      }
    } catch (e) {}
  };

  const handlePrev = () => {
    if (window.__4playEngine) window.__4playEngine.primeElements();
    const prev = (currentSongIndex - 1 + size(songs)) % size(songs);
    setCurrentSongIndex(prev);
  };

  const handleNext = () => {
    if (window.__4playEngine) window.__4playEngine.primeElements();
    const next = (currentSongIndex + 1) % size(songs);
    setCurrentSongIndex(next);
  };

  const handleShuffle = () => {
    socketServices.doShuffle(!isShuffle);
    const engine = window.__4playEngine;
    if (!isShuffle) {
      if (engine) engine.setBeforeShuffleSongs([...songs]);
      const shuffled = shuffleArray([...songs], currentSongIndex);
      if (engine) engine.shuffleInPlace(shuffled, currentSongIndex);
      setSongs(shuffled, currentSongIndex);
      toggleIsShuffle(true);
    } else {
      const original = engine?.getBeforeShuffleSongs();
      if (original?.length) {
        if (engine) engine.shuffleInPlace(original, currentSongIndex);
        setSongs(original, currentSongIndex);
      }
      toggleIsShuffle(false);
    }
  };

  const handleMuteToggle = () => {
    const engine = window.__4playEngine;
    if (!engine) return;
    const newMuted = !isMuted;
    setIsMuted(newMuted);
    const gainNode = engine.getCurrentGain();
    engine.setGainValue(gainNode, newMuted ? 0 : 1);
  };

  const handleProgressChange = (e) => {
    const engine = window.__4playEngine;
    if (!engine) return;
    const ref = engine.getCurrentAudioRef();
    if (ref?.current && !isNaN(ref.current.duration)) {
      ref.current.currentTime = ref.current.duration * parseFloat(e.target.value);
      if (engine.isFadeOutStarted.current) {
        engine.killAllFades();
        engine.setGainValue(engine.getCurrentGain(), 1);
        engine.setGainValue(engine.getOtherGain(), 0);
        engine.isFadeOutStarted.current = false;
      }
    }
  };

  const handleLike = async () => {
    if (userOffline) return showToaster("error", "You are offline");
    if (!song) return;
    const { id, index = "1.0" } = song;
    const alreadyLiked = isLiked;
    if (alreadyLiked) { deleteLikedSong(id); removeUnLikedSongFromPlaylist(song); }
    else { addLikedSong(id); addLikedSongToPlaylist(song); }
    const ok = alreadyLiked
      ? await removeSongFromFavorites({ song_id: id, song_name: song.title, artist_name: song.artist })
      : await addSongToFavorites({ song_id: id, index, song_name: song.title, artist_name: song.artist });
    if (!ok) {
      if (alreadyLiked) addLikedSong(id); else deleteLikedSong(id);
    }
  };

  const handleIgnore = async () => {
    if (userOffline) return showToaster("error", "You are offline");
    if (!song || !playList?.id) return;
    const key = song.id + playList.id;
    const newIgnore = !get(ignoredSongs, key, false);
    if (newIgnore) addIgnoreSong(key); else removeIgnoreSong(key);
    const ok = await ignoreSong(playList.id, song.id, { ignore: newIgnore });
    if (!ok) { if (newIgnore) removeIgnoreSong(key); else addIgnoreSong(key); }
  };

  const handleTouchStart = useCallback((e) => {
    touchStartY.current = e.touches[0].clientY;
    touchDelta.current = 0;
  }, []);

  const handleTouchMove = useCallback((e) => {
    const delta = e.touches[0].clientY - touchStartY.current;
    if (delta < 0) return;
    touchDelta.current = delta;
    if (containerRef.current) {
      containerRef.current.style.transform = `translateY(${delta}px)`;
      containerRef.current.style.opacity = Math.max(0.4, 1 - delta / 400);
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (touchDelta.current > 120) {
      if (containerRef.current) {
        containerRef.current.style.transition = 'transform 0.2s ease-out, opacity 0.2s ease-out';
        containerRef.current.style.transform = 'translateY(100%)';
        containerRef.current.style.opacity = '0';
      }
      setTimeout(onClose, 200);
    } else if (containerRef.current) {
      containerRef.current.style.transition = 'transform 0.2s ease-out, opacity 0.2s ease-out';
      containerRef.current.style.transform = '';
      containerRef.current.style.opacity = '';
      setTimeout(() => {
        if (containerRef.current) containerRef.current.style.transition = '';
      }, 200);
    }
  }, [onClose]);

  if (!song) return null;

  return (
    <div
      className={styles.overlay}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div className={styles.container} ref={containerRef}>
        <div className={styles.swipeIndicator} />

        <button className={styles.closeBtn} onClick={onClose}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
            <path d="M7 10l5 5 5-5" stroke="rgba(255,255,255,0.7)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>

        <div className={styles.artwork} key={song.id}>
          <ThumbNail
            name={song.title}
            color={song.color}
            imgSrc={`${MEDIA_URL}/songs_images/${song.imageFileName}`}
            hasImage={!!song.imageFileName}
            imageFileName={song.imageFileName}
          />
        </div>

        <div className={styles.playerRight}>
          <div className={styles.songInfo}>
            <p className={styles.title}>{song.title}</p>
            <p className={styles.artist}>{song.artist}</p>
          </div>

          <div className={styles.progressWrap}>
            <input
              ref={progressRef}
              type="range"
              min="0"
              max="1"
              step="0.001"
              defaultValue="0"
              className={styles.progressBar}
              onMouseUp={handleProgressChange}
              onTouchEnd={(e) => { e.stopPropagation(); handleProgressChange(e); }}
            />
            <div className={styles.times}>
              <span ref={currentTimeRef}>00:00</span>
              <span ref={totalTimeRef}>00:00</span>
            </div>
          </div>

          <div className={styles.controls}>
            <button className={styles.controlBtn} onClick={handleShuffle}>
              <Icons type={ICONS.SHUFFLE} fill={isShuffle ? "#6b3eb4" : "#ffffff"} />
            </button>
            <button className={styles.controlBtn} onClick={handlePrev}>
              <PrevIcon />
            </button>
            <button className={cx(styles.controlBtn, styles.playBtn)} onClick={handlePlayPause}>
              {isPlaying ? <PauseIcon /> : <PlayIcon />}
            </button>
            <button className={styles.controlBtn} onClick={handleNext}>
              <NextIcon />
            </button>
            <button className={styles.controlBtn} onClick={handleMuteToggle}>
              <Icons type={isMuted ? ICONS.MUTE : ICONS.VOLUME} fill="#ffffff" />
            </button>
          </div>

          <div className={styles.actions}>
            <button className={cx(styles.actionBtn, { [styles.activeLike]: isLiked })} onClick={handleLike}>
              <Icons type={ICONS.LIKE_SONG_LIST} />
              <span>{isLiked ? "Liked" : "Like"}</span>
            </button>
            {playList?.id && (
              <button className={cx(styles.actionBtn, { [styles.activeIgnore]: isIgnored })} onClick={handleIgnore}>
                <Icons type={ICONS.IGNORE_SONG} />
                <span>{isIgnored ? "Ignored" : "Ignore"}</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(FullPlayer);
